import { useState } from 'react';
import { api } from '../api';
import { Banner, Card, Empty, Loading, useAsync, when } from '../ui';

/** Operations: reconciliation history and anything that has stopped moving. */
export function Ops() {
  const dl = useAsync(() => api.deadLetters(), []);
  const rec = useAsync(() => api.reconciliation(), []);
  const [note, setNote] = useState<string | null>(null);

  return (
    <div className="stack">
      {note && <Banner tone="ok">{note}</Banner>}

      <Card title="Nightly reconciliation" flush>
        {rec.loading && <Loading />}
        {rec.error && <div style={{ padding: 18 }}><Banner tone="danger">{rec.error}</Banner></div>}
        {rec.data && (rec.data.runs.length === 0 ? (
          <Empty title="No reconciliation runs yet" hint="The job runs daily and proves the ledger against the provider's settlement file." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th><th>Programme</th><th>Result</th>
                  <th className="num">Matched</th><th className="num">Unmatched</th>
                  <th className="num">Resolved</th><th>Findings</th>
                </tr>
              </thead>
              <tbody>
                {rec.data.runs.map((r: any) => (
                  <tr key={`${r.programme}-${r.business_date}`}>
                    <td className="num">{String(r.business_date).slice(0, 10)}</td>
                    <td className="small">{r.programme}</td>
                    <td><span className={`pill ${r.status === 'balanced' ? 'ok' : 'danger'}`}>{r.status}</span></td>
                    <td className="num">{r.matched}</td>
                    <td className="num" style={r.unmatched > 0 ? { color: 'var(--danger)', fontWeight: 600 } : undefined}>{r.unmatched}</td>
                    <td className="num">{r.resolved}</td>
                    <td className="small">
                      {(r.report?.findings ?? []).length === 0
                        ? <span className="muted">Ledger reproduces the balance exactly</span>
                        : (r.report.findings as string[]).map((f, i) => <div key={i}>{f}</div>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </Card>

      <div className="grid cols-2">
        <Card title="Dead-lettered jobs" flush>
          {dl.loading && <Loading />}
          {dl.data && (dl.data.jobs.length === 0 ? (
            <Empty title="Queue is clean" hint="Nothing has exhausted its retries." />
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Job</th><th className="num">Attempts</th><th>Last error</th><th /></tr></thead>
                <tbody>
                  {dl.data.jobs.map((j: any) => (
                    <tr key={j.id}>
                      <td><div className="mono small">{j.kind}</div><div className="small muted">{when(j.failedAt)}</div></td>
                      <td className="num">{j.attempts}</td>
                      <td className="small" style={{ maxWidth: 220 }}>{String(j.lastError ?? '').slice(0, 140)}</td>
                      <td className="right">
                        <button className="btn sm" onClick={async () => {
                          await api.requeueJob(j.id);
                          setNote(`Job ${j.id} re-queued.`);
                          dl.reload();
                        }}>Retry</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Card>

        <Card title="Undelivered webhooks" flush>
          {dl.loading && <Loading />}
          {dl.data && (dl.data.webhooks.length === 0 ? (
            <Empty title="All delivered" hint="inDrive has acknowledged every event." />
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Event</th><th className="num">Attempts</th><th>Last error</th><th /></tr></thead>
                <tbody>
                  {dl.data.webhooks.map((w: any) => (
                    <tr key={w.id}>
                      <td>
                        <div className="mono small">{w.eventType}</div>
                        <div className="small muted">#{w.sequenceNo} · {when(w.createdAt)}</div>
                      </td>
                      <td className="num">{w.attempts}</td>
                      <td className="small" style={{ maxWidth: 200 }}>{String(w.lastError ?? '').slice(0, 120)}</td>
                      <td className="right">
                        <button className="btn sm" onClick={async () => {
                          await api.replayWebhook(w.id);
                          setNote(`Webhook #${w.sequenceNo} queued for redelivery.`);
                          dl.reload();
                        }}>Replay</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
